import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiSearch, FiPrinter, FiAward, FiAlertCircle, FiUser, FiHash } from "react-icons/fi";
import { resultAPI } from "../services/api";
import { calculateGrade } from "../utils/helpers";
import { useSiteSettings } from "../context/SiteSettingsContext";

const EXAM_TYPES = ["First Terminal", "Second Terminal", "Final Examination", "Pre-Board", "Unit Test"];
const CLASSES = ["Nursery", "KG", "Grade 1", "Grade 2", "Grade 3", "Grade 4", "Grade 5", "Grade 6", "Grade 7", "Grade 8", "Grade 9", "Grade 10", "Grade 11", "Grade 12"];

const getGradeColorStyle = (grade) => {
  if (["A+", "A"].includes(grade)) return "bg-[#2563EB] text-white";
  if (["B+", "B"].includes(grade)) return "bg-[#0EA5E9] text-white";
  if (["C+", "C"].includes(grade)) return "bg-[#1e40af] text-white";
  if (["D+", "D"].includes(grade)) return "bg-slate-500 text-white";
  return "bg-red-500 text-white";
};

const SectionLabel = ({ text }) => (
  <div className="flex items-center gap-3 mb-4">
    <div className="w-8 h-1 rounded-full bg-[#2563EB]" />
    <span className="text-[#2563EB] text-xs font-bold uppercase tracking-[0.25em]">{text}</span>
  </div>
);

const Results = () => {
  const { settings } = useSiteSettings();
  const [form, setForm] = useState({ rollNumber: "", class: "", section: "", examType: "", academicYear: "" });
  const [sections, setSections] = useState([]);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    if (!form.class) { setSections([]); return; }
    setSectionsLoading(true);
    setForm((prev) => ({ ...prev, section: "" }));
    resultAPI.getSections({ class: form.class })
      .then((res) => {
        const list = res.data?.data?.sections || [];
        setSections(list);
        if (list.length === 1) setForm((prev) => ({ ...prev, section: list[0] }));
      })
      .catch(() => setSections([]))
      .finally(() => setSectionsLoading(false));
  }, [form.class]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError("");
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!form.rollNumber.trim()) { setError("Please enter a roll number"); return; }
    if (!form.class) { setError("Please select a class"); return; }
    if (!form.examType) { setError("Please select an exam type"); return; }
    setLoading(true);
    setResult(null);
    setSearched(true);
    try {
      const params = { rollNumber: form.rollNumber, class: form.class, examType: form.examType };
      if (form.section) params.section = form.section;
      if (form.academicYear) params.academicYear = form.academicYear;
      const response = await resultAPI.search(params);
      const list = response.data?.data?.results;
      const data = Array.isArray(list) ? list[0] : response.data?.data?.result;
      if (data) {
        setResult(data);
      } else {
        setError("No published result found for the provided details. Please verify and try again.");
      }
    } catch (err) {
      if (err?.response?.status === 404) {
        setError("No result found for the provided details. Please check your roll number and try again.");
      } else {
        setError("Unable to fetch results. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => window.print();

  const subjects = result?.subjects || [];
  const totalFull = subjects.reduce((s, sub) => s + (sub.fullMarks || 0), 0);
  const totalObtained = subjects.reduce((s, sub) => s + (sub.obtainedMarks || 0), 0);
  const percentage = result?.percentage ?? (totalFull > 0 ? ((totalObtained / totalFull) * 100).toFixed(2) : 0);
  const overallGrade = result?.grade || calculateGrade(parseFloat(percentage));
  const passed = subjects.every((sub) => sub.obtainedMarks >= sub.passMarks);

  const fieldClass = "w-full px-5 py-4 bg-[#F0F4FF] border border-blue-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30 focus:border-[#2563EB] text-slate-900 font-medium placeholder-slate-400 text-sm transition-all appearance-none";

  return (
    <div className="bg-white">
      {/* Hero */}
      <div className="bg-[#1e3a8a] py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: "radial-gradient(circle, #ffffff 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#2563EB]" />
        <div className="relative max-w-7xl mx-auto px-8 sm:px-12 lg:px-16">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-1 rounded-full bg-sky-400" />
              <p className="text-sky-400 text-xs font-bold uppercase tracking-[0.25em]">Academic Results</p>
            </div>
            <h1 className="text-4xl sm:text-6xl font-bold text-white leading-tight mb-6">
              Result <span className="text-sky-300">Portal</span>
            </h1>
            <p className="text-blue-200/70 text-lg max-w-xl leading-relaxed">
              Enter your roll number to view your examination results.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-8 sm:px-12 lg:px-16 py-16 lg:py-24">
        {/* Search Form */}
        <div className="bg-[#F0F4FF] rounded-2xl p-8 lg:p-12 mb-12 border border-blue-100 no-print">
          <SectionLabel text="Find Your Record" />
          <h2 className="text-3xl font-bold text-[#0F172A] mb-8">
            Search <span className="text-[#2563EB]">Result</span>
          </h2>

          <form onSubmit={handleSearch} className="space-y-5">
            <input
              name="rollNumber"
              value={form.rollNumber}
              onChange={handleChange}
              placeholder="Roll Number (e.g. GA-2024-042) *"
              className={fieldClass}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <select name="class" value={form.class} onChange={handleChange} className={fieldClass}>
                <option value="">Select Class *</option>
                {CLASSES.map((c) => <option key={c}>{c}</option>)}
              </select>
              <select
                name="section"
                value={form.section}
                onChange={handleChange}
                disabled={!form.class || sectionsLoading}
                className={`${fieldClass} disabled:opacity-60`}
              >
                {sectionsLoading ? (
                  <option>Loading...</option>
                ) : (
                  <>
                    <option value="">{!form.class ? "Select Section (optional)" : sections.length === 0 ? "No sections found" : "All Sections"}</option>
                    {sections.map((s) => <option key={s} value={s}>Section {s}</option>)}
                  </>
                )}
              </select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <select name="examType" value={form.examType} onChange={handleChange} className={fieldClass}>
                <option value="">Select Exam *</option>
                {EXAM_TYPES.map((e) => <option key={e}>{e}</option>)}
              </select>
              <input
                name="academicYear"
                value={form.academicYear}
                onChange={handleChange}
                placeholder="Academic Year (optional)"
                className={fieldClass}
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-500 text-xs bg-red-50 border border-red-200 rounded-xl px-5 py-4">
                <FiAlertCircle className="w-4 h-4 shrink-0" />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 py-4 bg-[#2563EB] text-white font-bold rounded-xl uppercase tracking-widest text-xs hover:bg-[#1D4ED8] transition-colors disabled:opacity-70 shadow-md mt-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Searching...
                </>
              ) : (
                <>Search Result <FiSearch className="w-4 h-4" /></>
              )}
            </button>
          </form>
        </div>

        {/* Result Marksheet */}
        <AnimatePresence>
          {result && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-2xl border-2 border-[#1e3a8a] shadow-lg overflow-hidden print-card"
              id="result-card"
            >
              {/* Marksheet Header */}
              <div className="bg-[#1e3a8a] p-8 text-center relative overflow-hidden">
                <div className="absolute top-0 right-0 p-4 opacity-10">
                  <FiAward className="w-32 h-32 text-white" />
                </div>
                <div className="relative z-10">
                  <div className="flex items-center justify-center gap-4 mb-4">
                    <div className="w-10 h-10 bg-[#2563EB] rounded-xl flex items-center justify-center">
                      <FiAward className="w-5 h-5 text-white" />
                    </div>
                    <h2 className="text-white font-bold text-2xl">{settings?.schoolName || "Early Boarding School & Montessori"}</h2>
                  </div>
                  <p className="text-sky-300 text-xs font-bold uppercase tracking-[0.2em] mb-2">Official Mark Sheet</p>
                  <p className="text-blue-200/70 text-sm">{result.examType} — Academic Year: {result.academicYear}</p>
                </div>
              </div>

              {/* Student Info */}
              <div className="p-8 border-b border-blue-50 bg-[#F0F4FF]">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-sm">
                  {[
                    { icon: FiUser,  label: "Student Name",    value: result.studentName },
                    { icon: FiHash,  label: "Roll Number",     value: result.rollNumber },
                    { icon: null,    label: "Class & Section", value: result.class + (result.section ? ` - ${result.section}` : "") },
                    { icon: null,    label: "Academic Year",   value: result.academicYear },
                  ].map((item, i) => (
                    <div key={i}>
                      <p className="text-slate-400 text-[10px] uppercase tracking-widest font-bold mb-1 flex items-center gap-1.5">
                        {item.icon && <item.icon className="w-3 h-3 text-[#2563EB]" />}
                        {item.label}
                      </p>
                      <p className="font-bold text-[#0F172A]">{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Subject-wise Marks */}
              <div className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-2 h-2 bg-[#2563EB] rounded-full" />
                  <h3 className="font-bold text-[#0F172A] uppercase tracking-widest text-sm">Subject Details</h3>
                </div>

                <div className="overflow-x-auto rounded-xl border border-blue-100">
                  <table className="w-full text-sm text-left border-collapse">
                    <thead>
                      <tr className="bg-[#1e3a8a] text-white">
                        <th className="px-5 py-4 font-bold text-[10px] uppercase tracking-widest rounded-tl-xl">Subject</th>
                        <th className="px-5 py-4 font-bold text-[10px] uppercase tracking-widest text-center">Full Marks</th>
                        <th className="px-5 py-4 font-bold text-[10px] uppercase tracking-widest text-center">Pass Marks</th>
                        <th className="px-5 py-4 font-bold text-[10px] uppercase tracking-widest text-center">Obtained</th>
                        <th className="px-5 py-4 font-bold text-[10px] uppercase tracking-widest text-center rounded-tr-xl">Grade</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-blue-50 bg-white">
                      {subjects.map((sub, idx) => {
                        const subPct = sub.fullMarks > 0 ? (sub.obtainedMarks / sub.fullMarks) * 100 : 0;
                        const subGrade = sub.grade || calculateGrade(subPct);
                        const subPassed = sub.obtainedMarks >= sub.passMarks;
                        return (
                          <tr key={idx} className="hover:bg-[#F0F4FF] transition-colors">
                            <td className="px-5 py-4 font-bold text-[#0F172A]">{sub.name}</td>
                            <td className="px-5 py-4 text-center text-slate-500">{sub.fullMarks}</td>
                            <td className="px-5 py-4 text-center text-slate-500">{sub.passMarks}</td>
                            <td className={`px-5 py-4 text-center font-bold ${subPassed ? "text-[#0F172A]" : "text-red-500"}`}>
                              {sub.obtainedMarks}
                              {!subPassed && <span className="text-[10px] ml-1 text-red-500">*</span>}
                            </td>
                            <td className="px-5 py-4 text-center">
                              <span className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest rounded-lg ${getGradeColorStyle(subGrade)}`}>
                                {subGrade}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-[#F0F4FF] border-t-2 border-[#1e3a8a]">
                        <td className="px-5 py-4 font-bold text-[#0F172A] uppercase tracking-widest text-[10px]">Total</td>
                        <td className="px-5 py-4 text-center font-bold text-[#0F172A]">{totalFull}</td>
                        <td className="px-5 py-4 text-center text-slate-400">—</td>
                        <td className="px-5 py-4 text-center font-bold text-[#2563EB] text-lg">{totalObtained}</td>
                        <td className="px-5 py-4 text-center text-slate-400">—</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Summary Cards */}
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="bg-[#F0F4FF] rounded-2xl p-5 text-center border border-blue-100">
                    <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest mb-1">Total</p>
                    <p className="text-[#0F172A] font-bold text-xl">{totalObtained}/{totalFull}</p>
                  </div>
                  <div className="bg-[#F0F4FF] rounded-2xl p-5 text-center border border-blue-100">
                    <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest mb-1">Percentage</p>
                    <p className="text-[#0F172A] font-bold text-xl">{parseFloat(percentage).toFixed(2)}%</p>
                  </div>
                  <div className={`rounded-2xl p-5 text-center ${getGradeColorStyle(overallGrade)}`}>
                    <p className="text-white/70 text-[10px] font-bold uppercase tracking-widest mb-1">Grade</p>
                    <p className="font-bold text-2xl">{overallGrade}</p>
                  </div>
                  <div className={`rounded-2xl p-5 text-center ${passed ? "bg-[#2563EB] text-white" : "bg-red-500 text-white"}`}>
                    <p className="text-white/70 text-[10px] font-bold uppercase tracking-widest mb-1">Result</p>
                    <p className="font-bold text-xl uppercase tracking-widest">{passed ? "Pass" : "Fail"}</p>
                  </div>
                </div>

                {/* Position badges */}
                {(result.positionInClass || result.positionInSection || result.rank) && (
                  <div className="mt-6 flex flex-wrap gap-4">
                    {result.positionInClass && (
                      <div className="flex items-center gap-3 bg-[#F0F4FF] rounded-xl px-5 py-3 border border-blue-100">
                        <FiAward className="w-5 h-5 text-[#2563EB] shrink-0" />
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                          Class Position: <span className="text-lg font-bold text-[#0F172A] ml-2">{result.positionInClass}</span>
                        </span>
                      </div>
                    )}
                    {result.positionInSection && (
                      <div className="flex items-center gap-3 bg-[#F0F4FF] rounded-xl px-5 py-3 border border-blue-100">
                        <FiAward className="w-5 h-5 text-[#2563EB] shrink-0" />
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                          Section Position: <span className="text-lg font-bold text-[#0F172A] ml-2">{result.positionInSection}</span>
                        </span>
                      </div>
                    )}
                    {result.rank && !result.positionInClass && (
                      <div className="flex items-center gap-3 bg-[#F0F4FF] rounded-xl px-5 py-3 border border-blue-100">
                        <FiAward className="w-5 h-5 text-[#2563EB] shrink-0" />
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                          Rank: <span className="text-lg font-bold text-[#0F172A] ml-2">{result.rank}</span>
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Remarks */}
                {result.remarks && (
                  <div className="mt-8 bg-[#F0F4FF] rounded-xl p-6 border-l-4 border-[#2563EB]">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-[#2563EB] mb-2">Remarks</p>
                    <p className="text-sm font-medium text-slate-800 leading-relaxed">{result.remarks}</p>
                  </div>
                )}

                {/* Print Button */}
                <div className="mt-10 flex justify-end no-print">
                  <button
                    onClick={handlePrint}
                    className="inline-flex items-center gap-3 px-6 py-3 bg-[#F0F4FF] text-[#0F172A] text-xs font-bold rounded-xl uppercase tracking-widest border border-blue-100 hover:bg-[#2563EB] hover:text-white hover:border-[#2563EB] transition-all"
                  >
                    <FiPrinter className="w-4 h-4" />
                    Print Marksheet
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .print-card { box-shadow: none !important; border: none !important; }
        }
      `}</style>
    </div>
  );
};

export default Results;
